import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

import ChatbotContext from '../../../context/ChatbotContext/ChatbotContext';
import SettingsService from '../../../services/SettingsService/SettingsService';
import BotBuilderService from '../../../services/BotBuilderService/BotBuilderService';
import useAuthRoles from '../../../hooks/useAuthRoles';
import LabelWithInfo from '../../../components/LabelWithInfo/LabelWithInfo';
import CustomInput from '../../../components/CustomInput/CustomInput';
import Button from '../../../components/Button/Button';
import NotificationSnackbar from '../../../components/NotificationSnackbar/NotificationSnackbar';
import DeleteChatbotModal from '../../../components/DeleteChatbotModal/DeleteChatbotModal';
import ConfirmActionModal from '../../../components/ConfirmActionModal/ConfirmActionModal';
import { Switch } from '../../../components/ui/switch';
import PathVisibilitySection from './PathVisibilitySection';
import RateLimitSection from './RateLimitSection';

function GeneralTab(): React.ReactElement {
  const { currentChatbot, setCurrentChatbot, refreshChatbots } =
    React.useContext(ChatbotContext);
  const { userIsAdmin, userIsJapetoAdmin, userIsSuperAdmin } = useAuthRoles();
  const navigate = useNavigate();

  const [name, setName] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [disableModalOpen, setDisableModalOpen] = React.useState(false);
  const [isUpdatingEnabled, setIsUpdatingEnabled] = React.useState(false);

  React.useEffect(() => {
    setName(currentChatbot?.name ?? '');
  }, [currentChatbot]);

  const trimmedName = name.trim();
  const isUnchanged = trimmedName === (currentChatbot?.name ?? '');

  const handleSave = async (): Promise<void> => {
    if (!currentChatbot || !trimmedName || isUnchanged) return;

    setShowError(false);
    setShowSuccess(false);

    try {
      setIsLoading(true);
      await SettingsService.updateChatbotName(currentChatbot.id, trimmedName);
      await refreshChatbots();
      setSuccessMessage('Chatbot name updated');
      setShowSuccess(true);
    } catch (err: any) {
      setErrorMessage(err.message || 'Something went wrong');
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const updateEnabled = async (enabled: boolean): Promise<void> => {
    if (!currentChatbot) return;

    setShowError(false);
    setShowSuccess(false);

    try {
      setIsUpdatingEnabled(true);
      await SettingsService.updateChatbotEnabled(currentChatbot.id, enabled);
      await refreshChatbots();
      setSuccessMessage(enabled ? 'Chatbot enabled' : 'Chatbot disabled');
      setShowSuccess(true);
    } catch (err: any) {
      setErrorMessage(err.message || 'Something went wrong');
      setShowError(true);
    } finally {
      setIsUpdatingEnabled(false);
    }
  };

  const handleEnabledChange = (checked: boolean): void => {
    if (checked) {
      void updateEnabled(true);
    } else {
      setDisableModalOpen(true);
    }
  };

  const handleDeleteChatbot = async (): Promise<void> => {
    if (!currentChatbot) return;

    const { organisationId, id: deletedId } = currentChatbot;
    await BotBuilderService.deleteChatbot(deletedId);

    const orgChatbots = await BotBuilderService.getChatbotsInfo(organisationId);
    const next = orgChatbots.find((c) => c.id !== deletedId) ?? null;

    await setCurrentChatbot(next ? next.id : null);
    await refreshChatbots();

    navigate('/chats');
  };

  return (
    <div className='flex flex-1 flex-col'>
      <Helmet>
        <title>Japeto - Settings</title>
      </Helmet>

      <div className='w-full max-w-md'>
        <div className='space-y-2'>
          <LabelWithInfo
            htmlFor='chatbot-name'
            info='The internal name used to identify this chatbot in the dashboard.'
          >
            Chatbot name
          </LabelWithInfo>
          <CustomInput
            id='chatbot-name'
            name='chatbotName'
            value={name}
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className='flex justify-end mt-4'>
          <Button
            type='button'
            loading={isLoading}
            disabled={!trimmedName || isUnchanged}
            onClick={handleSave}
          >
            Save changes
          </Button>
        </div>
      </div>

      {userIsAdmin() && (
        <div className='w-full max-w-md border-t mt-8 pt-6'>
          <div className='flex items-center justify-between gap-4'>
            <div className='space-y-1'>
              <LabelWithInfo
                htmlFor='chatbot-enabled'
                info='When disabled, the chatbot is hidden on your website and will not respond to messages.'
              >
                Chatbot enabled
              </LabelWithInfo>
              <p className='text-sm text-muted-foreground'>
                {currentChatbot?.enabled
                  ? 'The chatbot is live on your website.'
                  : 'The chatbot is hidden on your website.'}
              </p>
            </div>
            <Switch
              id='chatbot-enabled'
              checked={!!currentChatbot?.enabled}
              disabled={!currentChatbot || isUpdatingEnabled}
              onCheckedChange={handleEnabledChange}
            />
          </div>
        </div>
      )}

      {userIsAdmin() && currentChatbot && (
        <PathVisibilitySection
          chatbot={currentChatbot}
          refreshChatbots={refreshChatbots}
          onSuccess={(message) => {
            setShowError(false);
            setSuccessMessage(message);
            setShowSuccess(true);
          }}
          onError={(message) => {
            setShowSuccess(false);
            setErrorMessage(message);
            setShowError(true);
          }}
        />
      )}

      {currentChatbot && (
        <RateLimitSection
          chatbot={currentChatbot}
          canEdit={userIsJapetoAdmin()}
          refreshChatbots={refreshChatbots}
          onSuccess={(message) => {
            setShowError(false);
            setSuccessMessage(message);
            setShowSuccess(true);
          }}
          onError={(message) => {
            setShowSuccess(false);
            setErrorMessage(message);
            setShowError(true);
          }}
        />
      )}

      {userIsSuperAdmin() && (
        <div className='w-full max-w-md border-t mt-8 pt-6'>
          <h3 className='text-sm font-semibold text-red-700'>Danger Zone</h3>
          <div className='flex items-center justify-between gap-4 mt-3'>
            <p className='text-sm text-muted-foreground'>
              Delete this chatbot and all its data. This cannot be undone.
            </p>
            <Button
              type='button'
              variant='destructive'
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete chatbot
            </Button>
          </div>
        </div>
      )}

      <ConfirmActionModal
        confirmationMessage={`Disable ${currentChatbot?.name ?? 'this chatbot'}?`}
        secondaryConfirmationMessage='It will be hidden on your website and stop responding to messages until it is enabled again.'
        action={() => updateEnabled(false)}
        open={disableModalOpen}
        setOpen={setDisableModalOpen}
      />

      <DeleteChatbotModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        chatbotName={currentChatbot?.name ?? ''}
        onConfirm={handleDeleteChatbot}
      />

      <NotificationSnackbar
        message={successMessage}
        severity='success'
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
      <NotificationSnackbar
        message={errorMessage}
        severity='error'
        open={showError}
        onClose={() => setShowError(false)}
      />
    </div>
  );
}

export default GeneralTab;
